import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { format } from "date-fns";
import {
  X,
  FileText,
  CreditCard,
  Download,
  Printer,
  CheckCircle,
  Clock,
  AlertCircle,
  Receipt,
  Info,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { InvoiceDetailsModalProps } from "@/modules/eventRegistration/features/EventWaitlist/components/InvoiceDetailsModal/types";
import styles from "./InvoiceDetailsModal.module.css";

export default function InvoiceDetailsModal({
  isOpen,
  onClose,
  invoices,
  payments,
  companyName,
  confirmationNumber,
}: InvoiceDetailsModalProps) {
  const [activeInvoiceIndex, setActiveInvoiceIndex] = useState(0);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Reset active invoice when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveInvoiceIndex(0);
    }
  }, [isOpen]);

  if (!invoices || invoices.length === 0) {
    return null;
  }

  const activeInvoice = invoices[activeInvoiceIndex];
  const hasMultipleInvoices = invoices.length > 1;

  const modalStyles = {
    content: {
      position: "fixed" as const,
      top: "0",
      left: "0",
      right: "0",
      bottom: "0",
      padding: "0",
      background: "white",
      overflow: "hidden",
      border: "none",
      borderRadius: "0",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 9999,
    },
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return format(new Date(dateString), "MMM d, yyyy");
  };

  const formatDateTime = (dateString: string) => {
    return format(new Date(dateString), "MMM d, yyyy 'at' h:mm a");
  };

  const getStatusBadge = (status: string | null) => {
    switch (status?.toLowerCase()) {
      case "paid":
      case "posted":
        return {
          icon: CheckCircle,
          className: styles.statusPaid,
          text: "Paid",
        };
      case "pending":
        return {
          icon: Clock,
          className: styles.statusPending,
          text: "Pending",
        };
      case "overdue":
        return {
          icon: AlertCircle,
          className: styles.statusOverdue,
          text: "Overdue",
        };
      default:
        return {
          icon: Info,
          className: styles.statusDefault,
          text: status || "Unknown",
        };
    }
  };

  const statusBadge = getStatusBadge(activeInvoice.status);
  const StatusIcon = statusBadge.icon;

  const subtotal = activeInvoice.lineItems.reduce(
    (sum, item) => sum + item.lineTotal,
    0,
  );
  const hasDiscount = activeInvoice.lineItems.some((item) => item.discountCode);

  // Calculate grand totals across all invoices
  const grandTotal = invoices.reduce((sum, inv) => sum + inv.totalAmount, 0);
  const totalPayments = payments.reduce(
    (sum, payment) => sum + payment.amount,
    0,
  );
  const totalCredits = invoices.reduce(
    (sum, inv) => sum + inv.creditMemoTotal,
    0,
  );
  const netGrandTotal = grandTotal - totalCredits;

  // Get payments for the active invoice
  const invoicePayments = payments.filter(
    (payment) => payment.invoiceId === activeInvoice.id,
  );

  const navigateInvoice = (direction: "prev" | "next") => {
    if (direction === "prev" && activeInvoiceIndex > 0) {
      setActiveInvoiceIndex(activeInvoiceIndex - 1);
    } else if (
      direction === "next" &&
      activeInvoiceIndex < invoices.length - 1
    ) {
      setActiveInvoiceIndex(activeInvoiceIndex + 1);
    }
  };

  return (
    <Modal
      contentLabel="Invoice Details"
      isOpen={isOpen}
      onRequestClose={onClose}
      style={modalStyles}
    >
      <div className={styles.modalContainer}>
        {/* Fixed Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              <div className={styles.headerIcon}>
                <FileText className={styles.headerIconSvg} />
              </div>
              <div>
                <h2 className={styles.headerTitle}>
                  {hasMultipleInvoices ? "Invoice Details" : "Invoice Details"}
                </h2>
                <p className={styles.headerSubtitle}>
                  {hasMultipleInvoices
                    ? `Viewing ${activeInvoiceIndex + 1} of ${invoices.length} invoices`
                    : `Invoice #${activeInvoice.invoiceNumber}`}
                </p>
              </div>
            </div>
            <div className={styles.headerActions}>
              <button
                className={styles.actionButton}
                onClick={() => window.print()}
                title="Print invoice"
              >
                <Printer className={styles.actionIcon} />
              </button>
              <button
                aria-label="Close modal"
                className={styles.closeButton}
                onClick={onClose}
                type="button"
              >
                <X className={styles.closeIcon} />
              </button>
            </div>
          </div>
        </div>

        {/* Invoice Navigation for Multiple Invoices */}
        {hasMultipleInvoices && (
          <div className={styles.invoiceNavigation}>
            <button
              className={styles.navButton}
              disabled={activeInvoiceIndex === 0}
              onClick={() => navigateInvoice("prev")}
            >
              <ChevronLeft className={styles.navIcon} />
              Previous
            </button>

            <div className={styles.invoiceTabs}>
              {invoices.map((invoice, index) => (
                <button
                  className={`${styles.invoiceTab} ${index === activeInvoiceIndex ? styles.activeTab : ""}`}
                  key={invoice.id}
                  onClick={() => setActiveInvoiceIndex(index)}
                >
                  <span className={styles.tabNumber}>{index + 1}</span>
                  <span className={styles.tabInvoiceNumber}>
                    #{invoice.invoiceNumber}
                  </span>
                  <span className={styles.tabAmount}>
                    {formatCurrency(invoice.totalAmount)}
                  </span>
                </button>
              ))}
            </div>

            <button
              className={styles.navButton}
              disabled={activeInvoiceIndex === invoices.length - 1}
              onClick={() => navigateInvoice("next")}
            >
              Next
              <ChevronRight className={styles.navIcon} />
            </button>
          </div>
        )}

        {/* Scrollable Content */}
        <div className={styles.modalBody}>
          <div className={styles.modalContent}>
            {/* Invoice Header Information */}
            <div className={styles.invoiceHeader}>
              <div className={styles.invoiceHeaderLeft}>
                <h3 className={styles.companyName}>{companyName}</h3>
                {confirmationNumber && (
                  <p className={styles.confirmationNumber}>
                    Confirmation: {confirmationNumber}
                  </p>
                )}
                <p className={styles.invoiceNumber}>
                  Invoice #{activeInvoice.invoiceNumber}
                </p>
              </div>
              <div className={styles.invoiceHeaderRight}>
                <div
                  className={`${styles.statusBadge} ${statusBadge.className}`}
                >
                  <StatusIcon className={styles.statusIcon} />
                  {statusBadge.text}
                </div>
              </div>
            </div>

            {/* Invoice Details Grid */}
            <div className={styles.detailsGrid}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Invoice Date</span>
                <span className={styles.detailValue}>
                  {formatDate(activeInvoice.invoiceDate)}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Invoice Amount</span>
                <span className={styles.detailValue}>
                  {formatCurrency(activeInvoice.totalAmount)}
                </span>
              </div>
              {activeInvoice.syncedAt && (
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Synced Date</span>
                  <span className={styles.detailValue}>
                    {formatDate(activeInvoice.syncedAt)}
                  </span>
                </div>
              )}
            </div>

            {/* Line Items Section */}
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>
                <Receipt className={styles.sectionIcon} />
                Line Items
              </h4>
              <div className={styles.lineItemsTable}>
                <div className={styles.tableHeader}>
                  <div className={styles.tableHeaderCell}>Description</div>
                  <div className={styles.tableHeaderCell}>Qty</div>
                  <div className={styles.tableHeaderCell}>Unit Price</div>
                  {hasDiscount && (
                    <div className={styles.tableHeaderCell}>Discount</div>
                  )}
                  <div className={styles.tableHeaderCell}>Total</div>
                </div>
                {activeInvoice.lineItems.map((item) => (
                  <div className={styles.tableRow} key={item.id}>
                    <div className={styles.tableCell}>
                      <p className={styles.itemDescription}>
                        {item.description}
                      </p>
                    </div>
                    <div className={styles.tableCell}>{item.quantity}</div>
                    <div className={styles.tableCell}>
                      {formatCurrency(item.unitPrice)}
                    </div>
                    {hasDiscount && (
                      <div className={styles.tableCell}>
                        {item.discountCode || "-"}
                      </div>
                    )}
                    <div className={styles.tableCell}>
                      {formatCurrency(item.lineTotal)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className={styles.totalsSection}>
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>Subtotal:</span>
                  <span className={styles.totalValue}>
                    {formatCurrency(subtotal)}
                  </span>
                </div>
                {activeInvoice.creditMemos.length > 0 && (
                  <>
                    <div className={styles.totalRow}>
                      <span className={styles.totalLabel}>Credit Memos:</span>
                      <span className={styles.totalValue}>
                        -{formatCurrency(activeInvoice.creditMemoTotal)}
                      </span>
                    </div>
                    <div
                      className={`${styles.totalRow} ${styles.totalRowFinal}`}
                    >
                      <span className={styles.totalLabel}>Net Amount:</span>
                      <span className={styles.totalValueLarge}>
                        {formatCurrency(activeInvoice.netAmount)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Credit Memos Section */}
            {activeInvoice.creditMemos.length > 0 && (
              <div className={styles.section}>
                <h4 className={styles.sectionTitle}>
                  <FileText className={styles.sectionIcon} />
                  Credit Memos
                </h4>
                <div className={styles.creditMemosList}>
                  {activeInvoice.creditMemos.map((memo) => (
                    <div className={styles.creditMemoItem} key={memo.id}>
                      <div className={styles.creditMemoHeader}>
                        <span className={styles.creditMemoAmount}>
                          -{formatCurrency(memo.amount)}
                        </span>
                        <span className={styles.creditMemoDate}>
                          {formatDate(memo.createdAt)}
                        </span>
                      </div>
                      <p className={styles.creditMemoReason}>{memo.reason}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment Details Section - Show payments for this invoice */}
            {invoicePayments.length > 0 && (
              <div className={styles.section}>
                <h4 className={styles.sectionTitle}>
                  <CreditCard className={styles.sectionIcon} />
                  Payments for This Invoice
                </h4>
                {invoicePayments.map((payment, index) => (
                  <div className={styles.paymentCard} key={payment.id}>
                    {invoicePayments.length > 1 && (
                      <p className={styles.paymentNumber}>
                        Payment {index + 1}
                      </p>
                    )}
                    <div className={styles.paymentGrid}>
                      <div className={styles.paymentItem}>
                        <span className={styles.paymentLabel}>
                          Transaction ID
                        </span>
                        <span className={styles.paymentValue}>
                          {payment.transactionId}
                        </span>
                      </div>
                      <div className={styles.paymentItem}>
                        <span className={styles.paymentLabel}>
                          Payment Amount
                        </span>
                        <span className={styles.paymentValue}>
                          {formatCurrency(payment.amount)}
                        </span>
                      </div>
                      {payment.cardType && payment.cardLast4 && (
                        <div className={styles.paymentItem}>
                          <span className={styles.paymentLabel}>
                            Payment Method
                          </span>
                          <span className={styles.paymentValue}>
                            {payment.cardType} •••• {payment.cardLast4}
                          </span>
                        </div>
                      )}
                      <div className={styles.paymentItem}>
                        <span className={styles.paymentLabel}>
                          Payment Date
                        </span>
                        <span className={styles.paymentValue}>
                          {formatDateTime(payment.createdAt)}
                        </span>
                      </div>
                      {payment.createdBy && (
                        <div className={styles.paymentItem}>
                          <span className={styles.paymentLabel}>
                            Processed By
                          </span>
                          <span className={styles.paymentValue}>
                            {payment.createdBy}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Notes Section */}
            {activeInvoice.notes && (
              <div className={styles.section}>
                <h4 className={styles.sectionTitle}>
                  <Info className={styles.sectionIcon} />
                  Notes
                </h4>
                <div className={styles.notesCard}>
                  <p className={styles.notesText}>{activeInvoice.notes}</p>
                </div>
              </div>
            )}

            {/* Grand Totals Summary - Only show if multiple invoices */}
            {hasMultipleInvoices && (
              <div className={styles.section}>
                <h4 className={styles.sectionTitle}>
                  <FileText className={styles.sectionIcon} />
                  Transaction Summary (All Invoices)
                </h4>
                <div className={styles.grandTotalsCard}>
                  <div className={styles.grandTotalRow}>
                    <span className={styles.grandTotalLabel}>
                      Total Invoiced:
                    </span>
                    <span className={styles.grandTotalValue}>
                      {formatCurrency(grandTotal)}
                    </span>
                  </div>
                  {totalCredits > 0 && (
                    <div className={styles.grandTotalRow}>
                      <span className={styles.grandTotalLabel}>
                        Total Credits:
                      </span>
                      <span className={styles.grandTotalValue}>
                        -{formatCurrency(totalCredits)}
                      </span>
                    </div>
                  )}
                  <div className={styles.grandTotalRow}>
                    <span className={styles.grandTotalLabel}>Net Total:</span>
                    <span className={styles.grandTotalValue}>
                      {formatCurrency(netGrandTotal)}
                    </span>
                  </div>
                  <div
                    className={`${styles.grandTotalRow} ${styles.grandTotalRowFinal}`}
                  >
                    <span className={styles.grandTotalLabel}>
                      Total Payments:
                    </span>
                    <span className={styles.grandTotalValueLarge}>
                      {formatCurrency(totalPayments)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Sync Status */}
            {activeInvoice.canBeSynced && (
              <div className={styles.syncStatusCard}>
                <Info className={styles.syncIcon} />
                <div>
                  <p className={styles.syncTitle}>Accounting Sync Status</p>
                  <p className={styles.syncText}>
                    {activeInvoice.syncedAt
                      ? `Synced on ${formatDate(activeInvoice.syncedAt)}`
                      : `Sync attempts: ${activeInvoice.syncAttempts}`}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
